import type { components } from '@countdown/api-client';
import { Required } from '@countdown/theme';
import { type ReactElement, type SyntheticEvent, useCallback, useState } from 'react';

import { ActionChoice, defaultActionId, seasonActions } from '@/components/ActionChoice';
import { PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  type ActionOutcome,
  LOAD_FAILED,
  NO_LIVE_ACTION,
  ROLE_MISSING,
  loadEmergencyStatus,
  participantRef,
  pullEmergencySwitch,
  useResource,
} from '@/lib/api';
import { SUPPRESSION_REASON_LABELS, formatDateTime } from '@/lib/labels';

type StatusView = components['schemas']['EmergencyStatusView'];
type BackofficeAction = components['schemas']['BackofficeActionView'];

/**
 * The emergency stop, on a screen (concept 9.6.4, Bereitschaftsplan §2.3).
 *
 * WHY THIS EXISTS ALONGSIDE THE CLI, NOT INSTEAD OF IT. `countdown-admin
 * emergency` stays the path the standby plan rests on: a stop reachable only
 * through a web application fails together with the web application. What this
 * screen changes is the first question of every incident (what is stopped
 * right now), which previously needed an SSH session at three in the morning.
 * Somebody who has to open a database to find out whether the dispatch is
 * already stopped either stops it twice or not at all.
 *
 * TWO SWITCHES FOR THE ACTION, DELIBERATELY. "Everything off" is two clicks.
 * A pause that also stopped the dispatch would stop the address confirmations,
 * and every address unconfirmed at that moment silently misses the draw pool
 * (§2.3). That loss cannot be repaired afterwards, so the interface does not
 * offer the convenient version.
 *
 * EVERY SWITCH DEMANDS A REASON. The field is one input above all of them
 * rather than one per button: during an incident the reason is the same for
 * every action taken in that minute, and a form that asked again per click
 * would collect "s" four times.
 *
 * NO NAMES ON THIS SCREEN. The status reports counts. Blocking one participant
 * is done by the address the complaint arrived with, or by the id from the
 * participant record, typed once and never echoed back: the answer says what
 * happened, not to whom.
 *
 * ONE ACTION AT A TIME, AND THE DISPATCH OF ALL OF THEM (D36). Several calendars run side by
 * side; the screen shows the one chosen above. "Versand aller Aktionen anhalten" is the
 * incident lever: one click holds every message of the cell, as the CLI does without --link.
 */
export function EmergencyPage({
  actions,
}: {
  readonly actions: readonly BackofficeAction[];
}): ReactElement {
  const [actionId, setActionId] = useState<string | null>(() => defaultActionId(actions));
  const load = useCallback(
    (api: Parameters<typeof loadEmergencyStatus>[0]) => loadEmergencyStatus(api, actionId ?? ''),
    [actionId],
  );
  const status = useResource<StatusView>(load);
  const [reason, setReason] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);

  const reload = status.status === 'error' ? undefined : status.reload;

  const pull = useCallback(
    (
      switchName: Parameters<typeof pullEmergencySwitch>[0],
      body: Parameters<typeof pullEmergencySwitch>[1],
    ): void => {
      setBusy(true);
      setOutcome(null);
      pullEmergencySwitch(switchName, body, actionId ?? undefined)
        .then((result) => {
          setOutcome(result);
          // Reload rather than patch: a switch changes more than the row it was clicked on,
          // and a hand-patched view would drift from the server's answer in the one place
          // where being wrong is expensive.
          reload?.();
        })
        .catch((error: unknown) => {
          console.error('[EmergencyPage] switch failed', switchName, error);
          setOutcome({ ok: false, message: 'Der Schalter konnte nicht betätigt werden.' });
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [reload, actionId],
  );

  /** Every action of the season at once: the incident lever of D36. */
  const stopAll = useCallback(
    (reasonText: string): void => {
      setBusy(true);
      setOutcome(null);
      Promise.all(
        seasonActions(actions).map((action) =>
          pullEmergencySwitch('stop-dispatch', { reason: reasonText }, action.action_id),
        ),
      )
        .then((results) => {
          const failed = results.find((result) => !result.ok);
          setOutcome(
            failed ?? { ok: true, message: 'Versand aller Aktionen angehalten.' },
          );
          reload?.();
        })
        .catch((error: unknown) => {
          console.error('[EmergencyPage] stop of every dispatch failed', error);
          setOutcome({ ok: false, message: 'Der Schalter konnte nicht betätigt werden.' });
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [actions, reload],
  );

  const trimmedReason = reason.trim();
  const reasonMissing = trimmedReason.length < 3;
  // Without an action the action switches can only answer 404. Blocking a participant
  // needs no action, and a complaint does not wait for the next season. A single door is taken
  // off on its own page (Aktionen, Aktion, Türchen), which names its action.
  const noAction = status.status === 'not_found';
  // After the end the dispatch switch still reaches the finished action, whose revoked-lead
  // notices still go out. Its calendar has nothing left to close.
  const ended = status.status === 'ready' && isEnded(status.data);
  const trimmedIdentifier = identifier.trim();

  if (status.status === 'forbidden') {
    return (
      <>
        <PageHeading title="Not-Aus" />
        <p className="text-sm text-muted-foreground">{ROLE_MISSING}</p>
      </>
    );
  }

  return (
    <>
      <PageHeading title="Not-Aus" />

      <ActionChoice
        actions={actions}
        value={actionId}
        disabled={busy}
        onChange={(id) => {
          setOutcome(null);
          setActionId(id);
        }}
      />

      {status.status === 'loading' ? (
        <p className="text-sm text-muted-foreground">Wird geladen …</p>
      ) : null}
      {status.status === 'error' ? (
        <p className="text-sm text-muted-foreground">{LOAD_FAILED}</p>
      ) : null}
      {noAction ? <p className="text-sm text-muted-foreground">{NO_LIVE_ACTION}</p> : null}
      {status.status === 'ready' ? <StatusBlock view={status.data} /> : null}

      <form
        className="mt-6 max-w-2xl"
        onSubmit={(event: SyntheticEvent<HTMLFormElement>) => {
          event.preventDefault();
        }}
      >
        <label className="block text-sm font-medium" htmlFor="begruendung">
          Begründung
          <Required />
        </label>
        <input
          className="mt-1 w-full border border-input bg-background px-3 py-2 text-sm"
          id="begruendung"
          aria-required="true"
          value={reason}
          disabled={busy}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />

        {noAction ? null : (
          <section className="mt-6">
            <h2 className="text-lg font-bold">Ganze Aktion</h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {ended ? null : (
                <>
                  <Button
                    variant="destructive"
                    type="button"
                    disabled={busy || reasonMissing}
                    onClick={() => {
                      pull('pause-action', { reason: trimmedReason });
                    }}
                  >
                    Kalender schließen
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy || reasonMissing}
                    onClick={() => {
                      pull('resume-action', { reason: trimmedReason });
                    }}
                  >
                    Kalender öffnen
                  </Button>
                </>
              )}
              <Button
                    variant="destructive"
                type="button"
                disabled={busy || reasonMissing}
                onClick={() => {
                  pull('stop-dispatch', { reason: trimmedReason });
                }}
              >
                Versand anhalten
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={busy || reasonMissing}
                onClick={() => {
                  pull('resume-dispatch', { reason: trimmedReason });
                }}
              >
                Versand freigeben
              </Button>
              {seasonActions(actions).length > 1 ? (
                <Button
                  variant="destructive"
                  type="button"
                  disabled={busy || reasonMissing}
                  onClick={() => {
                    stopAll(trimmedReason);
                  }}
                >
                  Versand aller Aktionen anhalten
                </Button>
              ) : null}
            </div>
          </section>
        )}

        <section className="mt-6">
          <h2 className="text-lg font-bold">Eine Teilnahme</h2>
          <label className="mt-3 block text-sm font-medium" htmlFor="adresse">
            E-Mail-Adresse oder Kennung
          </label>
          <input
            className="mt-1 w-full border border-input bg-background px-3 py-2 text-sm"
            id="adresse"
            autoComplete="off"
            value={identifier}
            disabled={busy}
            onChange={(event) => {
              setIdentifier(event.target.value);
            }}
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
                    variant="destructive"
              type="button"
              disabled={busy || reasonMissing || trimmedIdentifier === ''}
              onClick={() => {
                pull('block-participant', {
                  reason: trimmedReason,
                  ...participantRef(trimmedIdentifier),
                });
              }}
            >
              Teilnahme sperren
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={busy || reasonMissing || trimmedIdentifier === ''}
              onClick={() => {
                pull('unblock-participant', {
                  reason: trimmedReason,
                  ...participantRef(trimmedIdentifier),
                });
              }}
            >
              Sperre aufheben
            </Button>
          </div>
        </section>
      </form>

      {outcome !== null ? (
        <div
          className="mt-6 border border-border bg-surface-sunken px-4 py-3 text-sm"
          role="status"
          aria-live="polite"
        >
          {outcome.ok ? (outcome.message ?? 'Erledigt.') : outcome.message}
        </div>
      ) : null}
    </>
  );
}

function isEnded(view: StatusView): boolean {
  return view.action_status === 'finished' || view.action_status === 'archived';
}

function StatusBlock({ view }: { readonly view: StatusView }): ReactElement {
  return (
    <section className="max-w-2xl border border-border p-4">
      <h2 className="text-lg font-bold">{view.action_title}</h2>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm">
        <dt>Kalender</dt>
        <dd>
          {isEnded(view) ? (
            <StatusBadge tone="neutral">beendet</StatusBadge>
          ) : view.action_paused_at === null ? (
            <StatusBadge tone="success">läuft</StatusBadge>
          ) : (
            <StatusBadge tone="danger">
              pausiert seit {formatDateTime(view.action_paused_at)}
            </StatusBadge>
          )}
        </dd>
        <dt>Versand</dt>
        <dd>
          {view.dispatch_stopped_at === null ? (
            <StatusBadge tone="success">läuft</StatusBadge>
          ) : (
            <StatusBadge tone="danger">
              angehalten seit {formatDateTime(view.dispatch_stopped_at)}
            </StatusBadge>
          )}
        </dd>
        <dt>Türchen vom Netz</dt>
        <dd>
          {view.depublished_fields.length === 0
            ? 'keine'
            : view.depublished_fields.map((field) => field.position).join(', ')}
        </dd>
        <dt>Gesperrte Teilnahmen</dt>
        <dd>{view.blocked_participants}</dd>
        <dt>Harte Unzustellbarkeiten</dt>
        <dd>{view.hard_bounced_participants}</dd>
        <dt>Unterdrückte E-Mails</dt>
        <dd>
          {view.suppressed_addresses}
          {view.suppressions.length === 0 ? null : (
            <ul className="mt-1 text-muted-foreground">
              {view.suppressions.map((row) => (
                <li key={row.reason}>
                  {SUPPRESSION_REASON_LABELS[row.reason]}: {row.count}
                </li>
              ))}
            </ul>
          )}
        </dd>
      </dl>
    </section>
  );
}
